import { toTitleCaseString } from "../string/toTitleCaseString.ts";

/**
 * Check if a number is finite (any number except +-Infinity and NaN which are classified as numbers)
 * @returns boolean true if it is a finite number
 */
export const isFiniteNumber = (value: string | number): boolean =>
  Number.isFinite(Number.parseInt(String(value))) &&
  Number.isFinite(Number.parseFloat(String(value)));

/**
 * Validates if a number passes the provided bounds restrictions. Meant to be used with react-hook-form.
 * @param value a valid number
 * @param param2.min provide the number the value must be greater than or equal to
 * @param param2.max provide the number that value must be lesser than or equal to
 * @param fieldName the name of the field to be used in the error message
 * @returns boolean true if validation passed, error message otherwise
 */
export function validateNumericBounds(
  value: number,
  { min, max }: { min?: number; max?: number },
  fieldName?: string
) {
  if (min !== undefined && value < min) {
    return `${
      fieldName
        ? toTitleCaseString(fieldName, {
            intent: "ui",
          })
        : "Answer"
    } must be greater than or equal to ${min}.`;
  }

  if (max !== undefined && value > max) {
    return `${
      fieldName
        ? toTitleCaseString(fieldName, {
            intent: "ui",
          })
        : "Answer"
    } must be less than or equal to ${max}.`;
  }

  return true;
}

/**
 * Check if a number falls within a buffer range of a target number
 * @param value the number to check
 * @returns true if the number falls within +/- buffer range of the target
 * number
 */
export const isWithinBufferRangeOfTarget = (
  value: number,
  {
    target,
    buffer,
    inclusive = true,
  }: {
    /** target number to check against */
    target: number;
    /** the +/- range from the target number */
    buffer: number;
    /** include the ceiling/floor of the range? */
    inclusive?: boolean;
  }
) =>
  inclusive
    ? value <= target + buffer && value >= target - buffer
    : value < target + buffer && value > target - buffer;
